import cx from 'classnames';
import {isEmpty} from 'lodash';
import {Fragment, useCallback, useContext, useEffect, useMemo, useState} from 'react';
import {useTranslation} from 'react-i18next';

import {
    ParticipantOnlineStatusIcon, CopyText, ParticipantName, CreateClientConnectionButton,
    ParticipantPlatform,
    ParticipantDetailsModal,
    Icon,
    Tooltip,
    ParticipantAsyncRequestsHistoryModal,
    ConfirmButton
} from 'components';
import {UserContext} from 'context';
import {usePrevious} from 'hooks';
import {
    AgentSession, AnyParticipant, OnlineSession, ParticipantAsyncRequest, SessionAppointment, SessionHistory, SessionStatus
} from 'interfaces';
import {TRANSLATION_KEYS} from 'locale';
import {AudioVideoConnectionStatus} from 'pages/User/Conference/AudioVideoConnectionStatus';
import {ParticipantAsyncRequests} from 'pages/User/Conference/ParticipantAsyncRequests';
import {OnlineStatus, ParticipantRole} from 'types';
import {sessionUtils} from 'utils';

type Props = {
    participant: AnyParticipant;
    session?: AgentSession | OnlineSession | SessionAppointment | SessionHistory;
    localParticipant?: AnyParticipant;
    participantDetailsModalOpen?: boolean;
    local?: boolean;
    nameStyle?: 'base' | 'h3-bold-dimmed';
    spacing?: boolean;
    className?: string;
    iconClassName?: string;
    canUpdate?: boolean;
    canRemove?: boolean;
    fields?: {
        icon?: boolean;
        platform?: boolean;
        clientCopyId?: boolean;
        clientInfoModal?: boolean;
        joinLink?: boolean;
        createClientConnection?: boolean;
        onlineStatus?: boolean;
        offlineStatusText?: boolean;
        asyncRequestsResults?: boolean; // for session history async requests modal
        /**
         * UserSessionContext required
         */
        asyncRequests?: boolean;
        asyncRequestsReadonly?: boolean;
        /**
         * UserSessionContext required
         */
        audioVideoConnectionStatus?: boolean;
    },
    onUpdate?: (participant?: AnyParticipant) => void;
    onRemove?: (participant?: AnyParticipant) => void;
    onParticipantDetailsModalClose?: () => void;
    onSendInvite?: (participant: AnyParticipant) => void;
}

export type ParticipantInfoProps = Props;

export const ParticipantInfo: React.FC<Props> = ({
    session, participant, localParticipant, local, spacing = true, className: classNameProp, canRemove,
    iconClassName, participantDetailsModalOpen, fields = {}, nameStyle, canUpdate, onUpdate, onRemove,
    onParticipantDetailsModalClose, onSendInvite
}: Props) => {
    const {settings} = useContext(UserContext);
    const {t} = useTranslation();
    const [onlineStatus, setOnlineStatus] = useState<OnlineStatus>();
    const clientConnectionsEnabled = useMemo(() => settings.featuresAvailability.clientsConnections, [settings]);
    const className = useMemo(() => cx({'tw-my-2': spacing}, classNameProp), [spacing, classNameProp]);
    const [participantDetailsModalProps, setParticipantDetailsModalProps] = useState<{
        open: boolean,
        session?: AgentSession | OnlineSession | SessionAppointment,
        participant?: AnyParticipant
    }>({open: false});
    const [asyncRequestModalProps, setAsyncRequestsModalProps] = useState<{
        open: boolean,
        asyncRequests?: Partial<ParticipantAsyncRequest>[]
    }>({open: false});
    const prevParticipantDetailsPropsOpen = usePrevious(participantDetailsModalOpen);

    // Optional chaining is load-bearing: this dependency is evaluated on every render, and the participant is
    // missing whenever the session DTO has no localParticipant — a bare deref took the whole page down.
    const handleAsyncRequestsHistoryOpen = useCallback(() => {
        setAsyncRequestsModalProps({open: true, asyncRequests: participant?.asyncRequests});
    }, [participant?.asyncRequests]);

    const handleAsyncRequestsHistoryClose = useCallback(() => {
        setAsyncRequestsModalProps({open: false});
    }, []);

    const handleParticipantDetailsModalOpen = useCallback(() => {
        setParticipantDetailsModalProps({open: true, session, participant});
    }, [session, participant]);

    const handleParticipantDetailsModalClose = useCallback(() => {
        setParticipantDetailsModalProps({open: false});
        onParticipantDetailsModalClose?.();
    }, [onParticipantDetailsModalClose]);

    const handleSendInvite = useCallback(() => {
        setParticipantDetailsModalProps({open: false});
        onSendInvite?.(participant);
    }, [participant, onSendInvite]);

    useEffect(() => {
        if (participantDetailsModalOpen && !prevParticipantDetailsPropsOpen) {
            handleParticipantDetailsModalOpen();
        }
    }, [participantDetailsModalOpen, prevParticipantDetailsPropsOpen, handleParticipantDetailsModalOpen]);

    return (
        <Fragment>
            {sessionUtils.isUserParticipant(participant) &&
                <div className={className}>
                    <div className='tw-flex tw-flex-wrap'>
                        <div className={cx({'tw-mr-3': fields.platform}, 'tw-flex tw-items-center tw-flex-nowrap')}>
                            {fields.onlineStatus &&
                                <ParticipantOnlineStatusIcon
                                    participant={participant}
                                    session={session}
                                    reverseAlign
                                />}
                            <ParticipantName
                                participant={participant}
                                localParticipant={localParticipant}
                                local={local}
                                icon={fields.icon}
                                nameStyle={nameStyle}
                                iconClassName={iconClassName}
                            />
                        </div>
                        {fields.platform &&
                            <div className='tw-flex tw-flex-nowrap'>
                                <ParticipantPlatform participant={participant} local={local} />
                            </div>}
                    </div>
                </div>}
            {sessionUtils.isInviteeParticipant(participant) &&
                <div className={className}>
                    <div className='tw-flex tw-items-center tw-flex-wrap'>
                        <div className='tw-inline-flex tw-items-center tw-group tw-mr-3'>
                            {fields.onlineStatus &&
                                <ParticipantOnlineStatusIcon
                                    participant={participant}
                                    session={session}
                                    reverseAlign
                                    onChange={setOnlineStatus}
                                />}
                            <ParticipantName
                                participant={participant}
                                localParticipant={localParticipant}
                                local={local}
                                icon={fields.icon}
                                nameStyle={nameStyle}
                                iconClassName={cx(iconClassName, 'tw-mr-1')}
                                canUpdate={canUpdate}
                                onClick={fields.clientInfoModal ? handleParticipantDetailsModalOpen : null}
                                onUpdate={onUpdate}
                            />
                        </div>
                        {fields.clientCopyId && <CopyText text={participant?.data?.userInfo?.id} className='tw-mr-1' iconOnly />}
                        {clientConnectionsEnabled && fields.createClientConnection && participant.role === ParticipantRole.Client &&
                            <CreateClientConnectionButton
                                client={participant}
                                isSessionActive={SessionStatus.InSession === (session as OnlineSession)?.status}
                                clientConnectId={(session as OnlineSession)?.clientConnectId}
                                agentEmail={(sessionUtils.getAgentParticipant(session))?.data.email}
                                assignment={session?.assignment}
                                className='tw-mr-1'
                                iconProps={{size: nameStyle === 'base' ? 'sm' : 'md'}}
                            />}
                        {fields.asyncRequestsResults && !isEmpty(participant.asyncRequests) &&
                            <Tooltip message={t(TRANSLATION_KEYS.COMPONENTS.PARTICIPANT_ASYNC_REQUESTS_MODAL.TITLE)}>
                                <div className='tw-mr-1'>
                                    <Icon
                                        name='shield'
                                        size='sm'
                                        button
                                        onClick={handleAsyncRequestsHistoryOpen}
                                    />
                                </div>
                            </Tooltip>}
                        {fields.platform &&
                            <div className='tw-flex tw-flex-nowrap'>
                                <ParticipantPlatform participant={participant} local={local} osClassName='tw-mr-1' />
                            </div>}
                        {canRemove && !participant.removed &&
                            <div className='tw-ml-auto'>
                                <ConfirmButton
                                    iconProps={{
                                        name: 'x-circle',
                                        color: 'alert',
                                        size: 'sm',
                                        button: true,
                                        group: true,
                                        stopPropagation: true,
                                    }}
                                    onConfirm={() => onRemove(participant)}
                                />
                            </div>}
                    </div>
                    {fields.offlineStatusText && fields.onlineStatus && onlineStatus === OnlineStatus.Offline && !participant.removed &&
                        <div className='tw-pl-9 tw-text-alert'>{t(TRANSLATION_KEYS.COMPONENTS.ONLINE_STATE.OFFLINE)}</div>}
                    {participant.removed &&
                        <div className='tw-pl-9 tw-text-alert'>{t(TRANSLATION_KEYS.COMPONENTS.ONLINE_STATE.REMOVED)}</div>}
                    {(fields.audioVideoConnectionStatus || fields.asyncRequests) &&
                        <div className='tw-pl-9'>
                            {fields.audioVideoConnectionStatus &&
                                <AudioVideoConnectionStatus participant={participant} className='tw-mt-2' />}
                            {fields.asyncRequests &&
                                <ParticipantAsyncRequests
                                    className='tw-mt-2'
                                    participant={participant}
                                    readonly={fields.asyncRequestsReadonly}
                                />}
                        </div>}

                </div>}
            {fields.asyncRequestsResults &&
                <ParticipantAsyncRequestsHistoryModal
                    {...asyncRequestModalProps}
                    participant={participant}
                    sessionId={(session as OnlineSession)?.id || (session as SessionHistory)?.sessionId}
                    onClose={handleAsyncRequestsHistoryClose}
                />}
            {fields.clientInfoModal &&
                <ParticipantDetailsModal
                    {...participantDetailsModalProps}
                    showJoinLink={fields?.joinLink}
                    guestEnabled={settings.featuresAvailability.guestEnabled}
                    onClose={handleParticipantDetailsModalClose}
                    onSendInvite={handleSendInvite}
                />}
        </Fragment>
    );
};
